import { CheckIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Select as ChakraSelect,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { StatusTag } from 'components/StatusTag';
import { Pagination } from 'components/Table/Pagination';
import { useUserContext } from 'context/UserContextProvider';
import usePortalsQuery from 'hooks/usePortalsQuery';
import useWorkshopAccountsQuery, { GetWorkshopAccountsFilter } from 'hooks/useWorkshopAccountsQuery';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoIosCopy } from 'react-icons/io';
import { LuRotateCcw } from 'react-icons/lu';
import { MdOpenInNew } from 'react-icons/md';
import { Link as RouterLink } from 'react-router-dom';
import Select, { MultiValue } from 'react-select';
import { useCopyToClipboard, useLocalStorage } from 'usehooks-ts';

const CellWithCopyButton = ({ value, valueType }: { value: string; valueType: string }) => {
  const [showCheckIcon, setShowCheckIcon] = useState(false);
  const [, copy] = useCopyToClipboard();

  const SHOW_CHECKICON_DURATION = 700;

  const handleCopy = () => {
    copy(value ?? '');
    setShowCheckIcon(true);
    setTimeout(() => setShowCheckIcon(false), SHOW_CHECKICON_DURATION);
  };

  return (
    <HStack>
      <Text>{value}</Text>
      <IconButton icon={showCheckIcon ? <CheckIcon /> : <IoIosCopy />} aria-label={`Copy ${valueType} to clipboard button`} onClick={handleCopy} />
    </HStack>
  );
};

const rowsPerPageOptions = [5, 10, 20, 50, 100] as const;

type Props = {
  workshopId: string;
};

export const AccountsTable = ({ workshopId }: Props) => {
  const { t } = useTranslation();
  const { statusCodes } = useUserContext();
  const storageKey = 'workshop_accounts_filters';
  const initialFilters: GetWorkshopAccountsFilter = useMemo(
    () => ({
      workshopId,
      page: 1,
      itemsPerPage: 50,
      status: undefined,
      type: undefined,
      portalId: undefined,
    }),
    [workshopId],
  );

  const [filters, setFilters] = useLocalStorage<GetWorkshopAccountsFilter>(storageKey, initialFilters);
  const effectiveFilters = useMemo(() => ({ ...filters, workshopId }), [filters, workshopId]);
  const workshopAccountsQuery = useWorkshopAccountsQuery(workshopId, effectiveFilters);
  const portalsQuery = usePortalsQuery();
  const accounts = useMemo(() => workshopAccountsQuery.data?.data ?? [], [workshopAccountsQuery.data]);

  const portals = portalsQuery.data;

  const columnHelper = useMemo(() => createColumnHelper<(typeof accounts)[number]>(), []);

  const columns = useMemo(
    () => [
      columnHelper.accessor('portal', {
        header: () => t('workshop:accounts.accounts_table.table_head.portal_name'),
        cell: (info) => (
          <HStack>
            <Text>{info.getValue()?.name}</Text>
            <IconButton icon={<MdOpenInNew />} aria-label="open" size="sm" onClick={() => window.open(info.getValue()?.url, '_blank')} />
          </HStack>
        ),
      }),
      columnHelper.accessor('type', {
        header: () => t('workshop:accounts.accounts_table.table_head.type'),
        cell: (info) => t(`account:type.${info.getValue()}.label`),
      }),
      columnHelper.accessor('status', {
        header: () => t('workshop:accounts.accounts_table.table_head.status'),
        cell: (info) => <StatusTag resourceKey={'account'} currentStatus={info.getValue() ?? 0} />,
      }),
      columnHelper.accessor('username', {
        header: () => t('workshop:accounts.accounts_table.table_head.username'),
        cell: (info) => {
          return info.getValue() != null ? <CellWithCopyButton value={info.getValue()!} valueType="username" /> : <Text>—</Text>;
        },
      }),
      columnHelper.accessor('password', {
        header: () => t('workshop:accounts.accounts_table.table_head.password'),
        cell: (info) => {
          return info.getValue() != null ? <CellWithCopyButton value={info.getValue()!} valueType="password" /> : <Text>—</Text>;
        },
      }),
      columnHelper.display({
        id: 'actions',
        cell: (props) => (
          <Button as={RouterLink} to={`/app/op/account/${props.row.original.id}`}>
            {t('workshop:accounts.accounts_table.table_head.details_button')}
          </Button>
        ),
      }),
    ],
    [columnHelper, t],
  );

  const table = useReactTable({
    columns,
    data: accounts,
    getCoreRowModel: getCoreRowModel(),
  });

  type StatusLiteral = NonNullable<GetWorkshopAccountsFilter['status']>[number];
  type StatusOption = {
    value: StatusLiteral;
    label: string;
  };

  const statusOptions: StatusOption[] = [
    {
      value: 0,
      label: t('account:status.not_registered.label'),
    },
    {
      value: 50,
      label: t('account:status.not_available.label'),
    },
    {
      value: 100,
      label: t('account:status.registration_pending.label'),
    },
    {
      value: 150,
      label: t('account:status.registration_on_hold.label'),
    },
    {
      value: 200,
      label: t('account:status.registered.label'),
    },
    {
      value: 800,
      label: t('account:status.registration_rejected.label'),
    },
    {
      value: 700,
      label: t('account:status.blocked.label'),
    },
    {
      value: 900,
      label: t('account:status.deleted.label'),
    },
    {
      value: 600,
      label: t('account:status.deletion_pending.label'),
    },
    {
      value: 650,
      label: t('account:status.deletion_in_progress.label'),
    },
    {
      value: 675,
      label: t('account:status.deletion_on_hold.label'),
    },
  ];

  const selectedStatusOptions = statusOptions.filter((option) => filters.status?.includes(option.value));

  type TypeLiteral = NonNullable<GetWorkshopAccountsFilter['type']>[number];
  type TypeOption = {
    value: TypeLiteral;
    label: string;
  };

  const typeOptions: TypeOption[] = (statusCodes?.account_type ?? []).map((type) => ({
    value: type as TypeLiteral,
    label: t(`account:type.${type}.label`),
  }));

  const selectedTypeOptions = typeOptions.filter((option) => filters.type?.includes(option.value));

  const meta = workshopAccountsQuery.data?.meta;
  const currentPage = meta?.current_page ?? filters.page!;
  const currentPerPage = meta?.per_page ?? filters.itemsPerPage!;
  const totalPageCount = meta?.last_page ?? Math.ceil((meta?.total ?? 0) / currentPerPage);

  useEffect(() => {
    const storedPerPage = filters.itemsPerPage ?? initialFilters.itemsPerPage!;
    if (!rowsPerPageOptions.includes(storedPerPage as any)) {
      setFilters((prev) => ({
        ...prev,
        itemsPerPage: initialFilters.itemsPerPage!,
        page: initialFilters.page!,
      }));
      return;
    }
    if (meta?.last_page != null && currentPage > meta.last_page) {
      setFilters((prev) => ({ ...prev, page: meta.last_page }));
    }
  }, [currentPage, filters.itemsPerPage, initialFilters.itemsPerPage, initialFilters.page, meta?.last_page, setFilters]);

  return (
    <Box>
      <HStack columnGap="8" rowGap="4" flexWrap="wrap" alignItems="end" mb="4">
        <FormControl maxWidth="28ch">
          <FormLabel>{t('account:data_table.portal_name.label')}</FormLabel>
          <ChakraSelect
            placeholder={t('common:all')}
            value={filters.portalId ?? ''}
            onChange={(e) =>
              setFilters((value) => ({
                ...value,
                portalId: parseInt(e.target.value) || undefined,
                page: initialFilters.page,
              }))
            }
          >
            {portals?.map((portal) => (
              <option key={portal.id} value={portal.id}>
                {portal.name}
              </option>
            ))}
          </ChakraSelect>
        </FormControl>
        <FormControl maxWidth="28ch">
          <FormLabel>{t('account:data_table.type.label')}</FormLabel>
          <Select<TypeOption, true>
            isMulti={true}
            options={typeOptions}
            placeholder={t('common:all')}
            isClearable={true}
            aria-label="multiselect-filter"
            value={selectedTypeOptions ?? ''}
            menuPosition="fixed"
            onChange={(selected: MultiValue<TypeOption>) => {
              const newType: TypeLiteral[] = selected.map((option) => option.value);
              setFilters((prev) => ({
                ...prev,
                type: newType.length > 0 ? newType : undefined,
                page: initialFilters.page,
              }));
            }}
          />
        </FormControl>
        <FormControl maxWidth="28ch">
          <FormLabel>{t('account:data_table.status.label')}</FormLabel>
          <Select<StatusOption, true>
            isMulti={true}
            options={statusOptions}
            placeholder={t('common:all')}
            isClearable={true}
            aria-label="multiselect-filter"
            value={selectedStatusOptions ?? ''}
            menuPosition="fixed"
            onChange={(selected: MultiValue<StatusOption>) => {
              const newStatuses: StatusLiteral[] = selected.map((option) => option.value);
              setFilters((prev) => ({
                ...prev,
                status: newStatuses.length > 0 ? newStatuses : undefined,
                page: initialFilters.page,
              }));
            }}
          />
        </FormControl>
        <Button mt="4" onClick={() => setFilters(initialFilters)} leftIcon={<LuRotateCcw />}>
          {t('components:table.filters.reset')}
        </Button>
      </HStack>
      <TableContainer>
        <Table>
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th key={header.id}>{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}</Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td key={cell.id} width={cell.column.id === 'actions' ? '20ch' : undefined}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
      <Pagination
        page={currentPage}
        onPageChange={(page) => setFilters((value) => ({ ...value, page }))}
        rowsPerPage={currentPerPage}
        onRowsPerPageChange={(rowsPerPage) => setFilters((value) => ({ ...value, itemsPerPage: rowsPerPage, page: initialFilters.page }))}
        totalPageCount={totalPageCount}
        firstIndex={initialFilters.page!}
      />
    </Box>
  );
};
